import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Check, X, Ban, HandCoins } from 'lucide-react';
import { ChequeRecord, ChequeStatus } from '@/app/types/cheque';
import { format, parseISO } from 'date-fns';
import { RecordLinkIcon } from '@/app/components/RecordLinkIcon';

interface ColumnsProps {
  onClearCheque: (cheque: ChequeRecord) => void;
  onUpdateStatus: (id: number, status: 'bounced' | 'cancelled') => void;
  onDelete: (id: number) => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'dd MMM yyyy');
  } catch {
    return dateString;
  }
};

const getStatusBadge = (status: ChequeStatus) => {
  const configs = {
    [ChequeStatus.PENDING]: { variant: 'secondary' as const, className: 'bg-warning/10 text-warning hover:bg-warning/10' },
    [ChequeStatus.CLEARED]: { variant: 'default' as const, className: 'bg-success/10 text-success hover:bg-success/10' },
    [ChequeStatus.BOUNCED]: { variant: 'destructive' as const },
    [ChequeStatus.CANCELLED]: { variant: 'outline' as const },
  };

  const config = configs[status];
  return (
    <Badge variant={config.variant} className={config.className}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

export const getChequeColumns = ({
  onClearCheque,
  onUpdateStatus,
  onDelete,
}: ColumnsProps): ColumnDef<ChequeRecord>[] => [
  {
    accessorKey: 'cheque_number',
    header: 'Cheque #',
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue('cheque_number')}</div>
    ),
  },
  {
    accessorKey: 'party_name',
    header: 'Party',
    cell: ({ row }) => {
      const cheque = row.original;
      return (
        <div className="flex items-center gap-1">
          <span>{cheque.party_name || cheque.party?.name || 'N/A'}</span>
          {cheque.party_id && (
            <RecordLinkIcon recordType="party" recordId={cheque.party_id} size="sm" />
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => (
      <div className="text-right font-medium">{formatCurrency(row.getValue('amount'))}</div>
    ),
  },
  {
    accessorKey: 'bank_name',
    header: 'Bank',
    cell: ({ row }) => row.getValue('bank_name') || '-',
  },
  {
    accessorKey: 'cheque_date',
    header: 'Cheque Date',
    cell: ({ row }) => formatDate(row.getValue('cheque_date')),
  },
  {
    accessorKey: 'clearing_date',
    header: 'Clearing Date',
    cell: ({ row }) => formatDate(row.getValue('clearing_date')),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => getStatusBadge(row.getValue('status')),
  },
  {
    accessorKey: 'voucher_number',
    header: 'Payment #',
    cell: ({ row }) => {
      const cheque = row.original;
      if (cheque.voucher_number && cheque.payment_voucher_id) {
        return (
          <div className="flex items-center gap-1">
            <span className="text-sm">{cheque.voucher_number}</span>
            <RecordLinkIcon recordType="payment-in" recordId={cheque.payment_voucher_id} size="sm" />
          </div>
        );
      }
      return <span className="text-muted-foreground">-</span>;
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const cheque = row.original;
      const isPending = cheque.status === ChequeStatus.PENDING;
      const isCleared = cheque.status === ChequeStatus.CLEARED;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />

            {isPending && (
              <>
                <DropdownMenuItem onClick={() => onClearCheque(cheque)} className="text-success">
                  <HandCoins className="mr-2 h-4 w-4" />
                  Clear Cheque
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onUpdateStatus(cheque.id, 'bounced')} className="text-destructive">
                  <X className="mr-2 h-4 w-4" />
                  Mark as Bounced
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onUpdateStatus(cheque.id, 'cancelled')}>
                  <Ban className="mr-2 h-4 w-4" />
                  Cancel Cheque
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(cheque.id)} className="text-destructive">
                  Delete
                </DropdownMenuItem>
              </>
            )}

            {!isPending && !isCleared && (
              <DropdownMenuItem disabled className="text-muted-foreground">
                No actions available
              </DropdownMenuItem>
            )}

            {isCleared && cheque.payment_voucher_id && (
              <DropdownMenuItem asChild>
                <a href={`/payments-in/${cheque.payment_voucher_id}`}>
                  View Payment
                </a>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];